// Dispatcher recursivo de items de Questionnaire (Módulo H, Grupo 1) — la
// capa de renderizado que hace falta construir sobre `useQuestionnaireForm`
// (`@medplum/react-hooks`, dato puro, ya implementa enableWhen/repeats/
// initial/paginación). Alcance real, auditado contra los 21 Questionnaire
// del catálogo (ver plan): string, text, boolean, integer, decimal, date,
// display, group (1 nivel, con un caso repetible: 033 odontograma), choice
// (answerOption), open-choice (answerValueSet, con repeats en
// "diagnósticos secundarios"), time (024: hora de la información/negativa, `valueTime` HH:mm:ss). Tipos jamás usados hoy (dateTime/url/
// attachment/reference/quantity) caen en un input de reserva genérico, para
// no romper si algún formulario futuro los usa.
//
// GOTCHA REAL encontrado en verificación en vivo 2026-08-31 (el hook es
// mutable-ref por dentro, `onChangeAnswer`/`onAddGroup` no tiran error si
// se los llama mal — silenciosamente no hacen nada): `context` NO es "los
// hermanos de este item", es la RUTA DE ANCESTROS — la lista de
// QuestionnaireResponseItem de los grupos padre, desde la raíz, SIN
// incluir el propio nivel del item. Para un item de primer nivel,
// `context = []`. Por eso este componente recibe DOS arrays distintos:
// `hermanos` (los QuestionnaireResponseItem de ESTE nivel, para encontrar
// la respuesta actual del item) y `context` (la ruta de ancestros, para
// pasarle a onChangeAnswer/onAddGroup) — nunca el mismo array.
import type { MedplumClient } from '@medplum/core';
import { isQuestionEnabled } from '@medplum/react-hooks';
import type { QuestionnaireItem, QuestionnaireResponse, QuestionnaireResponseItem, QuestionnaireResponseItemAnswer } from '@medplum/fhirtypes';
import type { JSX } from 'react';
import { BotonDictado } from '@/components/BotonDictado';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import { InterpretacionAutomatica, TotalCalculado } from './CampoTotalCalculado';
import { fuentesDeTotal, totalDeInterpretacion } from './totalCalculado';
import { ValueSetCombobox, ValueSetComboboxMulti, type OpcionValueSet } from './ValueSetCombobox';

type OnChangeAnswer = (context: QuestionnaireResponseItem[], item: QuestionnaireItem, answer: QuestionnaireResponseItemAnswer[]) => void;
type OnAddGroup = (context: QuestionnaireResponseItem[], item: QuestionnaireItem) => void;

export interface ItemRendererProps {
  readonly item: QuestionnaireItem;
  /** Los QuestionnaireResponseItem de ESTE nivel de anidamiento — para encontrar la respuesta actual de `item` (búsqueda por linkId). */
  readonly hermanos: QuestionnaireResponseItem[];
  /** Ruta de ancestros (grupos padre desde la raíz, sin incluir este nivel) — lo que espera `onChangeAnswer`/`onAddGroup`. `[]` en el primer nivel. */
  readonly context: QuestionnaireResponseItem[];
  readonly questionnaireResponse: QuestionnaireResponse;
  readonly medplum: MedplumClient;
  readonly onChangeAnswer: OnChangeAnswer;
  readonly onAddGroup: OnAddGroup;
}

const ETIQUETA_SECCION = 'block text-xs font-mono uppercase tracking-wide text-hce-muted';

export function ItemRenderer(props: ItemRendererProps): JSX.Element | null {
  const { item, hermanos, context, questionnaireResponse, medplum, onChangeAnswer, onAddGroup } = props;

  if (!isQuestionEnabled(item, questionnaireResponse)) return null;

  if (item.type === 'display') {
    return <p className="text-sm text-hce-muted">{item.text}</p>;
  }

  if (item.type === 'group') {
    const hijos = item.item ?? [];
    if (item.repeats) {
      const instancias = hermanos.filter((ri) => ri.linkId === item.linkId);
      return (
        <div className="flex flex-col gap-3 border-l-2 border-hce-line pl-3">
          <span className={ETIQUETA_SECCION}>{item.text}</span>
          {instancias.map((instancia, i) => (
            <div key={instancia.id ?? `${item.linkId}-${i}`} className="flex flex-col gap-2 rounded-md border border-border p-3">
              {hijos.map((hijo) => (
                <ItemRenderer
                  key={hijo.linkId}
                  item={hijo}
                  hermanos={instancia.item ?? []}
                  context={[...context, instancia]}
                  questionnaireResponse={questionnaireResponse}
                  medplum={medplum}
                  onChangeAnswer={onChangeAnswer}
                  onAddGroup={onAddGroup}
                />
              ))}
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" onClick={() => onAddGroup(context, item)} className="w-fit">
            + Agregar
          </Button>
        </div>
      );
    }
    const responseItem = hermanos.find((ri) => ri.linkId === item.linkId);
    return (
      <fieldset className="flex flex-col gap-3">
        <legend className={`${ETIQUETA_SECCION} mb-1`}>{item.text}</legend>
        {hijos.map((hijo) => (
          <ItemRenderer
            key={hijo.linkId}
            item={hijo}
            hermanos={responseItem?.item ?? []}
            context={responseItem ? [...context, responseItem] : context}
            questionnaireResponse={questionnaireResponse}
            medplum={medplum}
            onChangeAnswer={onChangeAnswer}
            onAddGroup={onAddGroup}
          />
        ))}
      </fieldset>
    );
  }

  // Items hoja — todo lo que sigue lee `hermanos` para el valor actual, y usa `context` (ruta de ancestros) para escribir.
  const responseItem = hermanos.find((ri) => ri.linkId === item.linkId);
  const answer = responseItem?.answer ?? [];
  const set = (nuevaRespuesta: QuestionnaireResponseItemAnswer[]): void => onChangeAnswer(context, item, nuevaRespuesta);
  const etiqueta = (
    <Label htmlFor={item.linkId} className={ETIQUETA_SECCION}>
      {item.text}
      {item.required && <span className="text-hce-clay"> *</span>}
    </Label>
  );

  switch (item.type) {
    case 'boolean': {
      // Sin `required` nativo en el Checkbox a propósito: el `required` de
      // HTML en un checkbox exige "marcado", una semántica que rompe
      // cualquier booleano cuya respuesta legítima sea "No" (bug real
      // encontrado en vivo en la Fase 3, 024 — la declaración de
      // aceptación/negativa del consentimiento informado es "No" tan válida
      // como "Sí", y con `required` nativo era IMPOSIBLE guardar un "No").
      // `set` ahora siempre registra una respuesta explícita (true o false)
      // en cuanto el usuario toca el control — antes, destildar borraba la
      // respuesta entera (`[]`), indistinguible de "todavía no contestó".
      const respondido = answer.length > 0;
      const marcado = answer[0]?.valueBoolean ?? false;
      return (
        <div className="flex items-center gap-2">
          <Checkbox id={item.linkId} checked={respondido ? marcado : false} onCheckedChange={(v) => set([{ valueBoolean: v === true }])} />
          <Label htmlFor={item.linkId} className="text-sm text-hce-ink">
            {item.text}
            {item.required && <span className="text-hce-clay"> *</span>}
          </Label>
        </div>
      );
    }

    case 'integer': {
      const valor = answer[0]?.valueInteger;
      const fuentes = fuentesDeTotal(item);
      if (fuentes) return <TotalCalculado item={item} fuentes={fuentes} respuesta={questionnaireResponse} actual={valor} onChange={set} />;
      return (
        <div className="flex flex-col gap-1">
          {etiqueta}
          <Input
            id={item.linkId}
            type="number"
            step={1}
            value={valor ?? ''}
            required={item.required}
            onChange={(e) => set(e.target.value === '' ? [] : [{ valueInteger: parseInt(e.target.value, 10) }])}
          />
        </div>
      );
    }

    case 'decimal': {
      const valor = answer[0]?.valueDecimal;
      return (
        <div className="flex flex-col gap-1">
          {etiqueta}
          <Input
            id={item.linkId}
            type="number"
            step="any"
            value={valor ?? ''}
            required={item.required}
            onChange={(e) => set(e.target.value === '' ? [] : [{ valueDecimal: parseFloat(e.target.value) }])}
          />
        </div>
      );
    }

    case 'date': {
      const valor = answer[0]?.valueDate ?? '';
      return (
        <div className="flex flex-col gap-1">
          {etiqueta}
          <Input id={item.linkId} type="date" value={valor} required={item.required} onChange={(e) => set(e.target.value ? [{ valueDate: e.target.value }] : [])} />
        </div>
      );
    }

    case 'time': {
      // FHIR `time` es HH:mm:ss; el input nativo entrega HH:mm.
      const valor = answer[0]?.valueTime?.slice(0, 5) ?? '';
      return (
        <div className="flex flex-col gap-1">
          {etiqueta}
          <Input id={item.linkId} type="time" value={valor} required={item.required} onChange={(e) => set(e.target.value ? [{ valueTime: `${e.target.value}:00` }] : [])} />
        </div>
      );
    }

    case 'text': {
      const valor = answer[0]?.valueString ?? '';
      return (
        <div className="flex flex-col gap-1">
          {etiqueta}
          <div className="flex items-start gap-2">
            <Textarea
              id={item.linkId}
              value={valor}
              required={item.required}
              onChange={(e) => set(e.target.value ? [{ valueString: e.target.value }] : [])}
              className="flex-1"
              rows={3}
            />
            <BotonDictado onTranscrito={(texto) => set([{ valueString: valor ? `${valor} ${texto}` : texto }])} />
          </div>
        </div>
      );
    }

    case 'choice':
    case 'open-choice': {
      if (item.answerValueSet) {
        const esMulti = item.type === 'open-choice' && item.repeats;
        if (esMulti) {
          const valores: OpcionValueSet[] = answer
            .map((a) => (a.valueCoding ? { code: a.valueCoding.code ?? '', display: a.valueCoding.display ?? a.valueCoding.code ?? '', system: a.valueCoding.system } : a.valueString ? { code: '', display: a.valueString } : null))
            .filter((v): v is OpcionValueSet => v !== null);
          return (
            <div className="flex flex-col gap-1">
              {etiqueta}
              <ValueSetComboboxMulti
                medplum={medplum}
                binding={item.answerValueSet}
                placeholder={item.text ?? ''}
                valores={valores}
                onChange={(nuevos) =>
                  set(nuevos.map((v): QuestionnaireResponseItemAnswer => (v.code ? { valueCoding: { system: v.system, code: v.code, display: v.display } } : { valueString: v.display })))
                }
              />
            </div>
          );
        }
        const primero = answer[0];
        const valorActual: OpcionValueSet | null = primero?.valueCoding
          ? { code: primero.valueCoding.code ?? '', display: primero.valueCoding.display ?? primero.valueCoding.code ?? '', system: primero.valueCoding.system }
          : primero?.valueString
            ? { code: '', display: primero.valueString }
            : null;
        return (
          <div className="flex flex-col gap-1">
            {etiqueta}
            <ValueSetCombobox
              medplum={medplum}
              binding={item.answerValueSet}
              placeholder={item.text ?? ''}
              valor={valorActual}
              permitirTextoLibre={item.type === 'open-choice'}
              onChange={(nuevo) => set(nuevo ? [nuevo.code ? { valueCoding: { system: nuevo.system, code: nuevo.code, display: nuevo.display } } : { valueString: nuevo.display }] : [])}
            />
          </div>
        );
      }
      // choice/open-choice respaldado por answerOption (vocabulario chico y cerrado, local al Questionnaire).
      const opciones = item.answerOption ?? [];
      const valorActual = answer[0]?.valueCoding?.code ?? answer[0]?.valueString ?? '';
      if (totalDeInterpretacion(item)) {
        // Interpretación automática: sale del total, no se elige a mano.
        return (
          <div className="flex flex-col gap-1">
            <InterpretacionAutomatica item={item} respuesta={questionnaireResponse} actualCodigo={answer[0]?.valueCoding?.code} onChange={set} />
            {etiqueta}
            <p className="text-sm text-hce-ink">{answer[0]?.valueCoding?.display ?? '—'}</p>
          </div>
        );
      }
      return (
        <div className="flex flex-col gap-1">
          {etiqueta}
          <RadioGroup
            value={valorActual}
            onValueChange={(v) => {
              const opcion = opciones.find((o) => (o.valueCoding?.code ?? o.valueString) === v);
              set(opcion?.valueCoding ? [{ valueCoding: opcion.valueCoding }] : opcion?.valueString ? [{ valueString: opcion.valueString }] : []);
            }}
          >
            {opciones.map((op, i) => {
              const val = op.valueCoding?.code ?? op.valueString ?? String(i);
              const etiquetaOp = op.valueCoding?.display ?? op.valueCoding?.code ?? op.valueString ?? String(i);
              return (
                <div key={val} className="flex items-center gap-2">
                  <RadioGroupItem value={val} id={`${item.linkId}-${val}`} />
                  <Label htmlFor={`${item.linkId}-${val}`} className="text-sm font-normal text-hce-ink">
                    {etiquetaOp}
                  </Label>
                </div>
              );
            })}
          </RadioGroup>
        </div>
      );
    }

    case 'string':
    default: {
      // 'string' y cualquier tipo no contemplado arriba (dateTime/time/url/
      // attachment/reference/quantity/question) caen acá como input de
      // reserva genérico — nunca usados hoy, pero no deben romper si algún
      // formulario futuro los trae.
      const valor = answer[0]?.valueString ?? '';
      return (
        <div className="flex flex-col gap-1">
          {etiqueta}
          <div className="flex items-center gap-2">
            <Input id={item.linkId} value={valor} required={item.required} onChange={(e) => set(e.target.value ? [{ valueString: e.target.value }] : [])} className="flex-1" />
            <BotonDictado onTranscrito={(texto) => set([{ valueString: valor ? `${valor} ${texto}` : texto }])} />
          </div>
        </div>
      );
    }
  }
}
